// ImportPreviewOverlay.jsx
import React from "react";

/* ---------- helpers ---------- */

function toHHMM(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function toDDMMYYYY(d) {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function weekdayName(d) {
  return d.toLocaleDateString("de-DE", { weekday: "long" });
}

/* ---------- component ---------- */

export default function ImportPreviewOverlay({
  open,
  importing,
  events,             // API-shaped payloads
  onClose,
  onEdit,             // (index) => void
  onDelete,           // (index) => void   <-- NEW
  onCommit,           // () => void
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div
        className="absolute left-1/2 top-16 w-[min(860px,95vw)] -translate-x-1/2 rounded-2xl border border-black/10 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-black/10 px-6 py-4">
          <div className="text-lg font-semibold text-gray-900">
            Vorschau importierter Termine ({Array.isArray(events) ? events.length : 0})
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Schließen
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-auto px-6 py-4">
          {!Array.isArray(events) || events.length === 0 ? (
            <div className="text-sm text-gray-600">Keine Termine erkannt.</div>
          ) : (
            <div className="space-y-3">
              {events.map((ev, idx) => {
                const title = ev.title || "(Kein Titel)";
                const start = ev.start ? new Date(ev.start) : null;
                const end = ev.end ? new Date(ev.end) : null;

                const when =
                  start && end
                    ? `${weekdayName(start)}, ${toDDMMYYYY(start)}, ${toHHMM(start)} → ${toHHMM(end)}`
                    : start
                      ? `${weekdayName(start)}, ${toDDMMYYYY(start)}`
                      : "—";

                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-xl border border-black/10 bg-white px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">
                        {title}
                      </div>

                      <div className="text-sm text-gray-600">
                        {when}
                      </div>

                      {ev.description ? (
                        <div className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {ev.description}
                        </div>
                      ) : null}
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit?.(idx)}
                        disabled={importing}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                      >
                        Bearbeiten
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete?.(idx)}
                        disabled={importing}
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-black/10 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
          >
            Abbrechen
          </button>

          <button
            type="button"
            onClick={onCommit}
            disabled={importing || !Array.isArray(events) || events.length === 0}
            className="rounded-xl bg-[#84bbff] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#5d9ef7] disabled:opacity-60"
          >
            {importing ? "Speichert..." : "Alle im Kalender speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
